import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout, Card, Button, ProjectWorkflowStatusBadge } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { documentApi } from '../services/api';
import type { AdminLibraryCompany, DocumentRecord, DmsFolder } from '../types';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

type DocSortField = 'createdAt' | 'originalName' | 'size' | 'uploadedByName';
type SortDirection = 'asc' | 'desc';

interface SelectedNode {
  projectId: string;
  contractNumber?: number;
  folderId?: string;
}

const PREVIEWABLE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.doc', '.xls', '.xlsx', '.ppt', '.pptx'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const WORD_EXTENSIONS = ['.doc', '.docx'];
const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];
const PPTX_EXTENSIONS = ['.ppt', '.pptx'];

async function parsePptxToHtml(arrayBuffer: ArrayBuffer, t: (key: string) => string): Promise<string> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const slideFiles = Object.keys(zip.files)
    .filter(name => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)/)?.[1] || '0');
      const numB = parseInt(b.match(/slide(\d+)/)?.[1] || '0');
      return numA - numB;
    });

  if (slideFiles.length === 0) return `<p>${t('adminDocumentLibrary.noSlides')}</p>`;

  let html = '';
  for (let i = 0; i < slideFiles.length; i++) {
    const xmlStr = await zip.files[slideFiles[i]].async('text');
    const texts: string[] = [];
    const regex = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g;
    let match;
    while ((match = regex.exec(xmlStr)) !== null) {
      const text = match[1].trim();
      if (text) texts.push(text);
    }
    html += `<div style="border:1px solid #dee2e6;border-radius:8px;padding:1.5rem;margin-bottom:1rem;background:#f8f9fa;">`;
    html += `<div style="font-size:0.85rem;color:#6c757d;margin-bottom:0.75rem;font-weight:600;">Slide ${i + 1}</div>`;
    if (texts.length > 0) {
      html += texts.map(t => `<p style="margin:0.25rem 0">${t.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('');
    } else {
      html += `<p style="color:#adb5bd;font-style:italic;">${t('adminDocumentLibrary.emptySlide')}</p>`;
    }
    html += `</div>`;
  }
  return html;
}

function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.substring(idx).toLowerCase() : '';
}

function getFileIcon(name: string): string {
  const ext = getFileExtension(name);
  if (['.pdf'].includes(ext)) return 'feather-file-text';
  if (['.doc', '.docx', '.txt'].includes(ext)) return 'feather-file-text';
  if (['.xls', '.xlsx', '.csv'].includes(ext)) return 'feather-file-text';
  if (['.ppt', '.pptx'].includes(ext)) return 'feather-file-text';
  if (['.jpg', '.jpeg', '.png', '.heic'].includes(ext)) return 'feather-image';
  return 'feather-file';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
}

export function AdminDocumentLibrary() {
  const { t } = useTranslation();

  // Tree state
  const [treeData, setTreeData] = useState<AdminLibraryCompany[]>([]);
  const [isLoadingTree, setIsLoadingTree] = useState(true);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  // Document state
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [renameDoc, setRenameDoc] = useState<DocumentRecord | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<DocumentRecord | null>(null);
  const [docSortField, setDocSortField] = useState<DocSortField>('createdAt');
  const [docSortDirection, setDocSortDirection] = useState<SortDirection>('desc');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // Admin folder state
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderTarget, setRenameFolderTarget] = useState<DmsFolder | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<DmsFolder | null>(null);

  // Derive selected project metadata from tree
  const selectedMeta = useMemo(() => {
    if (!selectedNode) return null;
    for (const company of treeData) {
      const project = company.projects.find(p => p.projectId === selectedNode.projectId);
      if (project) {
        return { company, project };
      }
    }
    return null;
  }, [selectedNode, treeData]);

  // Load tree data
  const loadTree = useCallback(async () => {
    try {
      const data = await documentApi.getAdminLibraryTree();
      setTreeData(data);
    } catch (error) {
      console.error('Failed to load library tree:', error);
    } finally {
      setIsLoadingTree(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // Load documents when a node is selected
  const loadDocuments = useCallback(async () => {
    if (!selectedNode) {
      setDocuments([]);
      return;
    }
    try {
      const contractNum = selectedNode.contractNumber !== undefined ? selectedNode.contractNumber : null;
      const folderId = selectedNode.folderId || null;
      let docs: DocumentRecord[];
      if (folderId) {
        docs = await documentApi.listDocuments(selectedNode.projectId, null, folderId);
      } else if (contractNum !== null) {
        docs = await documentApi.listDocuments(selectedNode.projectId, contractNum, null);
      } else {
        // Project-level files only (contractNumber = NaN signals project-level in API)
        docs = await documentApi.listDocuments(selectedNode.projectId, NaN, null);
      }
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  }, [selectedNode]);

  useEffect(() => {
    loadDocuments();
    setSelectedDocIds(new Set());
  }, [loadDocuments]);

  // Sorted documents
  const sortedDocuments = useMemo(() => {
    const result = [...documents];
    result.sort((a, b) => {
      let aVal: any = (a as any)[docSortField];
      let bVal: any = (b as any)[docSortField];
      if (docSortField === 'size') {
        return docSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      if (aVal < bVal) return docSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return docSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [documents, docSortField, docSortDirection]);

  // Handlers
  const toggleCompany = (companyNumber: number) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyNumber)) next.delete(companyNumber);
      else next.add(companyNumber);
      return next;
    });
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
        // Auto-select project files node so metadata shows immediately
        selectNode({ projectId });
      }
      return next;
    });
  };

  const selectNode = (node: SelectedNode) => {
    setSelectedNode(node);
    setSelectedDocIds(new Set());
  };

  const isNodeSelected = (projectId: string, contractNumber?: number, folderId?: string) => {
    if (!selectedNode) return false;
    if (selectedNode.projectId !== projectId) return false;
    if (folderId) return selectedNode.folderId === folderId;
    if (contractNumber !== undefined) return selectedNode.contractNumber === contractNumber && !selectedNode.folderId;
    return selectedNode.contractNumber === undefined && !selectedNode.folderId;
  };

  // File upload
  const handleFileUpload = async (files: FileList | File[]) => {
    if (!selectedNode) return;
    setIsUploading(true);
    setMessage(null);
    try {
      const contractNum = selectedNode.contractNumber !== undefined ? selectedNode.contractNumber : null;
      const folderId = selectedNode.folderId || null;
      await documentApi.uploadFiles(selectedNode.projectId, Array.from(files), contractNum, folderId);
      setMessage({ type: 'success', text: t('documents.upload.success') });
      await loadDocuments();
      await loadTree();
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: t('documents.upload.error') });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDocSelect = (docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedDocIds.size === sortedDocuments.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(sortedDocuments.map(d => d.id)));
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedDocIds.size === 0) return;
    if (selectedDocIds.size === 1) {
      const doc = documents.find(d => d.id === Array.from(selectedDocIds)[0]);
      if (doc) await documentApi.downloadFile(doc.id, doc.originalName);
    } else {
      await documentApi.downloadZip(Array.from(selectedDocIds));
    }
  };

  const handlePreview = async (doc: DocumentRecord) => {
    try {
      const blob = await documentApi.getFileBlob(doc.id);
      const ext = getFileExtension(doc.originalName);

      if (WORD_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewDoc(doc);
        setPreviewHtml(result.value);
        setPreviewUrl(null);
      } else if (EXCEL_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        let html = '';
        for (const name of workbook.SheetNames) {
          const sheet = workbook.Sheets[name];
          if (workbook.SheetNames.length > 1) html += `<h5 style="margin-top:1rem">${name}</h5>`;
          html += XLSX.utils.sheet_to_html(sheet, { editable: false });
        }
        setPreviewDoc(doc);
        setPreviewHtml(html);
        setPreviewUrl(null);
      } else if (PPTX_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const html = await parsePptxToHtml(arrayBuffer, t);
        setPreviewDoc(doc);
        setPreviewHtml(html);
        setPreviewUrl(null);
      } else {
        const url = URL.createObjectURL(blob);
        setPreviewDoc(doc);
        setPreviewUrl(url);
        setPreviewHtml(null);
      }
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewHtml(null);
  };

  const handleRenameSubmit = async () => {
    if (!renameDoc || !renameValue.trim()) return;
    setMessage(null);
    try {
      await documentApi.renameDocument(renameDoc.id, renameValue.trim());
      setMessage({ type: 'success', text: t('documents.rename.success') });
      setRenameDoc(null);
      setRenameValue('');
      await loadDocuments();
    } catch (error) {
      console.error('Rename error:', error);
      setMessage({ type: 'error', text: t('documents.rename.error') });
    }
  };

  const handleDeleteDocument = (doc: DocumentRecord) => {
    setDeleteConfirmDoc(doc);
  };

  const confirmDeleteDocument = async () => {
    if (!deleteConfirmDoc) return;
    setDeleteConfirmDoc(null);
    setMessage(null);
    try {
      await documentApi.deleteDocument(deleteConfirmDoc.id);
      setMessage({ type: 'success', text: t('documents.delete.success') });
      setSelectedDocIds(prev => {
        const next = new Set(prev);
        next.delete(deleteConfirmDoc.id);
        return next;
      });
      await loadDocuments();
      await loadTree();
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: t('documents.delete.error') });
    }
  };

  const handleDocSort = (field: DocSortField) => {
    if (docSortField === field) {
      setDocSortDirection(docSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDocSortField(field);
      setDocSortDirection(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  // Admin folder handlers
  const handleCreateFolder = async () => {
    if (!selectedNode || !newFolderName.trim()) return;
    setMessage(null);
    try {
      await documentApi.createFolder(selectedNode.projectId, newFolderName.trim());
      setMessage({ type: 'success', text: t('documents.folders.createSuccess') });
      setShowCreateFolder(false);
      setNewFolderName('');
      await loadTree();
    } catch (error) {
      console.error('Create folder error:', error);
      setMessage({ type: 'error', text: t('documents.folders.createError') });
    }
  };

  const handleRenameFolderSubmit = async () => {
    if (!renameFolderTarget || !renameFolderValue.trim()) return;
    setMessage(null);
    try {
      await documentApi.renameFolder(renameFolderTarget.id, renameFolderValue.trim());
      setMessage({ type: 'success', text: t('documents.folders.renameSuccess') });
      setRenameFolderTarget(null);
      setRenameFolderValue('');
      await loadTree();
    } catch (error) {
      console.error('Rename folder error:', error);
      setMessage({ type: 'error', text: t('documents.folders.renameError') });
    }
  };

  const handleDeleteFolderConfirm = async () => {
    if (!deleteFolderTarget) return;
    setMessage(null);
    try {
      await documentApi.deleteFolder(deleteFolderTarget.id);
      setMessage({ type: 'success', text: t('documents.folders.deleteSuccess') });
      // If the deleted folder was selected, clear selection
      if (selectedNode?.folderId === deleteFolderTarget.id) {
        setSelectedNode(null);
      }
      setDeleteFolderTarget(null);
      await loadTree();
      await loadDocuments();
    } catch (error) {
      console.error('Delete folder error:', error);
      setMessage({ type: 'error', text: t('documents.folders.deleteError') });
    }
  };

  if (isLoadingTree) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminDocumentLibrary.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminDocumentLibrary.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminDocumentLibrary.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Topbar */}
      {selectedNode && (
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
              <i className="feather-upload me-2"></i>
              {isUploading ? t('documents.upload.uploading') : t('documents.upload.button')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.heic"
            />
            {selectedDocIds.size > 0 && (
              <Button variant="light-brand" onClick={handleDownloadSelected}>
                <i className="feather-download me-2"></i>
                {t('documents.actions.downloadSelected')} ({selectedDocIds.size})
              </Button>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted d-none d-sm-inline">{t('documents.filter.sortBy')}:</small>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={docSortField}
              onChange={e => setDocSortField(e.target.value as DocSortField)}
            >
              <option value="createdAt">{t('documents.filter.sortDate')}</option>
              <option value="originalName">{t('documents.filter.sortName')}</option>
              <option value="size">{t('documents.filter.sortSize')}</option>
              <option value="uploadedByName">{t('documents.filter.sortUploader')}</option>
            </select>
            <button
              className="btn btn-sm btn-light"
              onClick={() => setDocSortDirection(docSortDirection === 'asc' ? 'desc' : 'asc')}
            >
              <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'}`}></i>
            </button>
          </div>
        </div>
      )}

      {/* Project Metadata Card */}
      {selectedMeta && (
        <Card className="mb-3">
          <div className="card-body py-2">
            <div className="row align-items-center g-3">
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block">{t('adminDocumentLibrary.meta.customer')}</small>
                <Link
                  to={`/admin/customers?customerNumber=${selectedMeta.project.customerNumber}&companyNumber=${selectedMeta.company.companyNumber}`}
                  className="fw-semibold text-decoration-none"
                >
                  {selectedMeta.project.customerName || '—'}
                  <i className="feather-external-link ms-1" style={{ fontSize: '0.75rem' }}></i>
                </Link>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block">{t('adminDocumentLibrary.meta.totalContractAmount')}</small>
                <span className="fw-semibold">{formatCurrency(selectedMeta.project.totalContractAmount)}</span>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block">{t('adminDocumentLibrary.meta.status')}</small>
                <ProjectWorkflowStatusBadge project={selectedMeta.project} />
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block">{t('adminDocumentLibrary.meta.company')}</small>
                <Link
                  to={`/admin/company-list?companyNumber=${selectedMeta.company.companyNumber}`}
                  className="fw-semibold text-decoration-none"
                >
                  {selectedMeta.company.companyName}
                  <i className="feather-external-link ms-1" style={{ fontSize: '0.75rem' }}></i>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="row">
        {/* Left: Tree Panel */}
        <div className="col-md-4 col-lg-3">
          <Card>
            <div className="card-body p-0">
              {treeData.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="feather-briefcase fs-1 d-block mb-2"></i>
                  {t('adminDocumentLibrary.noCompanies')}
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {treeData.map(company => {
                    const isCompanyExpanded = expandedCompanies.has(company.companyNumber);
                    return (
                      <React.Fragment key={company.companyNumber}>
                        {/* Company row */}
                        <button
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center fw-semibold"
                          onClick={() => toggleCompany(company.companyNumber)}
                        >
                          <span className="text-truncate">
                            <i className={`feather-chevron-${isCompanyExpanded ? 'down' : 'right'} me-1`} style={{ fontSize: '0.8rem' }}></i>
                            <i className="feather-briefcase me-2"></i>
                            {company.companyName}
                          </span>
                          <span className="badge bg-secondary rounded-pill">
                            {company.projects.reduce((sum, p) => sum + p.totalFileCount, 0)}
                          </span>
                        </button>

                        {/* Projects */}
                        {isCompanyExpanded && company.projects.map(project => {
                          const isProjectExpanded = expandedProjects.has(project.projectId);
                          return (
                            <React.Fragment key={project.projectId}>
                              {/* Project row */}
                              <button
                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                style={{ paddingLeft: '2rem' }}
                                onClick={() => toggleProject(project.projectId)}
                              >
                                <span className="text-truncate">
                                  <i className={`feather-chevron-${isProjectExpanded ? 'down' : 'right'} me-1`} style={{ fontSize: '0.8rem' }}></i>
                                  <i className="feather-folder me-2"></i>
                                  {project.projectNumber} - {project.description}
                                </span>
                                <span className="badge bg-secondary rounded-pill">
                                  {project.totalFileCount}
                                </span>
                              </button>

                              {/* Project children */}
                              {isProjectExpanded && (
                                <>
                                  {/* Project files */}
                                  <button
                                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isNodeSelected(project.projectId) ? 'active' : ''}`}
                                    style={{ paddingLeft: '3.5rem' }}
                                    onClick={() => selectNode({ projectId: project.projectId })}
                                  >
                                    <span className="text-truncate">
                                      <i className="feather-file me-2"></i>
                                      {t('adminDocumentLibrary.projectFiles')}
                                    </span>
                                    <span className="badge bg-secondary rounded-pill">
                                      {project.projectFileCount}
                                    </span>
                                  </button>

                                  {/* Contracts */}
                                  {project.contracts.map(c => (
                                    <button
                                      key={c.contractNumber}
                                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isNodeSelected(project.projectId, c.contractNumber) ? 'active' : ''}`}
                                      style={{ paddingLeft: '3.5rem' }}
                                      onClick={() => selectNode({ projectId: project.projectId, contractNumber: c.contractNumber })}
                                    >
                                      <span className="text-truncate">
                                        <i className="feather-folder me-2"></i>
                                        {t('documents.folderTree.contract')} {c.contractNumber}
                                      </span>
                                      <span className="badge bg-secondary rounded-pill">
                                        {c.fileCount}
                                      </span>
                                    </button>
                                  ))}

                                  {/* Admin folders */}
                                  {project.folders.map(f => (
                                    <button
                                      key={f.id}
                                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isNodeSelected(project.projectId, undefined, f.id) ? 'active' : ''}`}
                                      style={{ paddingLeft: '3.5rem' }}
                                      onClick={() => selectNode({ projectId: project.projectId, folderId: f.id })}
                                    >
                                      <span className="d-flex align-items-center text-truncate">
                                        <i className="feather-folder-plus me-2"></i>
                                        <span className="text-truncate">{f.name}</span>
                                      </span>
                                      <span className="d-flex align-items-center gap-1">
                                        <span className="badge bg-secondary rounded-pill">{f.fileCount}</span>
                                        <span className="d-flex gap-0">
                                          <span
                                            role="button"
                                            className="px-1"
                                            title={t('documents.folders.rename')}
                                            onClick={(e) => { e.stopPropagation(); setRenameFolderTarget(f); setRenameFolderValue(f.name); }}
                                          >
                                            <i className="feather-edit-2" style={{ fontSize: '0.75rem' }}></i>
                                          </span>
                                          <span
                                            role="button"
                                            className="px-1 text-danger"
                                            title={t('documents.folders.delete')}
                                            onClick={(e) => { e.stopPropagation(); setDeleteFolderTarget(f); }}
                                          >
                                            <i className="feather-trash-2" style={{ fontSize: '0.75rem' }}></i>
                                          </span>
                                        </span>
                                      </span>
                                    </button>
                                  ))}

                                  {/* Create folder button */}
                                  <div className="px-2 py-1" style={{ paddingLeft: '3.5rem' }}>
                                    <button
                                      className="btn btn-sm btn-light-primary w-100"
                                      onClick={() => {
                                        // Make sure this project is the selected context for folder creation
                                        setSelectedNode(prev => prev?.projectId === project.projectId ? prev : { projectId: project.projectId });
                                        setShowCreateFolder(true);
                                        setNewFolderName('');
                                      }}
                                    >
                                      <i className="feather-plus me-1"></i>
                                      {t('documents.folders.create')}
                                    </button>
                                  </div>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right: Document List */}
        <div className="col-md-8 col-lg-9">
          <Card>
            <div
              className={`card-body${isDragging ? ' bg-light border border-primary border-dashed' : ''}`}
              onDragEnter={selectedNode ? handleDragEnter : undefined}
              onDragOver={selectedNode ? handleDragOver : undefined}
              onDragLeave={selectedNode ? handleDragLeave : undefined}
              onDrop={selectedNode ? handleDrop : undefined}
              style={{ minHeight: '300px' }}
            >
              {isDragging && (
                <div className="text-center py-5">
                  <i className="feather-upload fs-1 text-primary mb-3 d-block"></i>
                  <h5 className="text-primary">{t('documents.upload.dragDrop')}</h5>
                </div>
              )}

              {!isDragging && !selectedNode && (
                <div className="text-center py-5">
                  <i className="feather-book-open fs-1 text-muted mb-3 d-block"></i>
                  <h5>{t('adminDocumentLibrary.selectFolder')}</h5>
                </div>
              )}

              {!isDragging && selectedNode && sortedDocuments.length === 0 && (
                <div className="text-center py-5">
                  <i className="feather-folder fs-1 text-muted mb-3 d-block"></i>
                  <h5>{t('documents.list.noFiles')}</h5>
                  <p className="text-muted">{t('documents.list.noFilesDescription')}</p>
                </div>
              )}

              {!isDragging && selectedNode && sortedDocuments.length > 0 && (
                <>
                  {/* Desktop table */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedDocIds.size === sortedDocuments.length && sortedDocuments.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th onClick={() => handleDocSort('originalName')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.name')}
                            {docSortField === 'originalName' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th onClick={() => handleDocSort('size')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.size')}
                            {docSortField === 'size' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th onClick={() => handleDocSort('uploadedByName')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.uploadedBy')}
                            {docSortField === 'uploadedByName' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th onClick={() => handleDocSort('createdAt')} style={{ cursor: 'pointer' }}>
                            {t('documents.list.date')}
                            {docSortField === 'createdAt' && <i className={`feather-chevron-${docSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                          </th>
                          <th>{t('documents.list.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedDocuments.map(doc => (
                          <tr key={doc.id}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedDocIds.has(doc.id)}
                                onChange={() => handleDocSelect(doc.id)}
                              />
                            </td>
                            <td>
                              <i className={`${getFileIcon(doc.originalName)} me-2`}></i>
                              {doc.originalName}
                            </td>
                            <td>{formatFileSize(doc.size)}</td>
                            <td>{doc.uploadedByName}</td>
                            <td>{new Date(doc.createdAt).toLocaleDateString('sv-SE')}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-light"
                                  title={t('documents.actions.download')}
                                  onClick={() => documentApi.downloadFile(doc.id, doc.originalName)}
                                >
                                  <i className="feather-download"></i>
                                </button>
                                {PREVIEWABLE_EXTENSIONS.includes(getFileExtension(doc.originalName)) && (
                                  <button
                                    className="btn btn-sm btn-light"
                                    title={t('documents.actions.preview')}
                                    onClick={() => handlePreview(doc)}
                                  >
                                    <i className="feather-eye"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-light"
                                  title={t('documents.actions.rename')}
                                  onClick={() => { setRenameDoc(doc); setRenameValue(doc.originalName); }}
                                >
                                  <i className="feather-edit-2"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-light text-danger"
                                  title={t('documents.actions.delete')}
                                  onClick={() => handleDeleteDocument(doc)}
                                >
                                  <i className="feather-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="d-md-none">
                    <div className="d-flex align-items-center mb-2 px-1">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={selectedDocIds.size === sortedDocuments.length && sortedDocuments.length > 0}
                        onChange={handleSelectAll}
                      />
                      <small className="text-muted">{t('documents.actions.selectAll')}</small>
                    </div>
                    {sortedDocuments.map(doc => (
                      <div
                        key={doc.id}
                        className={`border rounded p-3 mb-2 ${selectedDocIds.has(doc.id) ? 'border-primary bg-light' : ''}`}
                      >
                        <div className="d-flex align-items-start gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input mt-1 flex-shrink-0"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={() => handleDocSelect(doc.id)}
                          />
                          <div className="flex-grow-1 min-width-0">
                            <div className="fw-medium text-break">
                              <i className={`${getFileIcon(doc.originalName)} me-1`}></i>
                              {doc.originalName}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                              <small className="text-muted">{formatFileSize(doc.size)}</small>
                              <small className="text-muted">{doc.uploadedByName}</small>
                              <small className="text-muted">{new Date(doc.createdAt).toLocaleDateString('sv-SE')}</small>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex gap-1 mt-2 ms-4">
                          <button
                            className="btn btn-sm btn-light"
                            title={t('documents.actions.download')}
                            onClick={() => documentApi.downloadFile(doc.id, doc.originalName)}
                          >
                            <i className="feather-download"></i>
                          </button>
                          {PREVIEWABLE_EXTENSIONS.includes(getFileExtension(doc.originalName)) && (
                            <button
                              className="btn btn-sm btn-light"
                              title={t('documents.actions.preview')}
                              onClick={() => handlePreview(doc)}
                            >
                              <i className="feather-eye"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-light"
                            title={t('documents.actions.rename')}
                            onClick={() => { setRenameDoc(doc); setRenameValue(doc.originalName); }}
                          >
                            <i className="feather-edit-2"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-light text-danger"
                            title={t('documents.actions.delete')}
                            onClick={() => handleDeleteDocument(doc)}
                          >
                            <i className="feather-trash-2"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      {previewDoc && (previewUrl || previewHtml) && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('documents.preview.title')} - {previewDoc.originalName}
                </h5>
                <button type="button" className="btn-close" onClick={closePreview}></button>
              </div>
              <div className="modal-body" style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                {previewHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ textAlign: 'left' }} />
                ) : IMAGE_EXTENSIONS.includes(getFileExtension(previewDoc.originalName)) ? (
                  <div className="text-center">
                    <img src={previewUrl!} alt={previewDoc.originalName} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                  </div>
                ) : getFileExtension(previewDoc.originalName) === '.pdf' ? (
                  <iframe src={previewUrl!} style={{ width: '100%', height: '70vh', border: 'none' }} title={previewDoc.originalName} />
                ) : (
                  <p className="text-muted text-center">{t('documents.preview.notSupported')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameDoc && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.rename.title')}</h5>
                <button type="button" className="btn-close" onClick={() => setRenameDoc(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.rename.label')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(); }}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setRenameDoc(null)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleRenameSubmit}>
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmDoc && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.actions.delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteConfirmDoc(null)}></button>
              </div>
              <div className="modal-body">
                <p>{t('documents.delete.confirm')}</p>
                <p className="text-muted mb-0">
                  <i className={`${getFileIcon(deleteConfirmDoc.originalName)} me-2`}></i>
                  {deleteConfirmDoc.originalName}
                </p>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setDeleteConfirmDoc(null)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="danger" onClick={confirmDeleteDocument}>
                  <i className="feather-trash-2 me-2"></i>
                  {t('documents.actions.delete')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.create')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateFolder(false)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.folders.nameLabel')}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('documents.folders.namePlaceholder')}
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setShowCreateFolder(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                  {t('documents.folders.create')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {renameFolderTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.rename')}</h5>
                <button type="button" className="btn-close" onClick={() => setRenameFolderTarget(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">{t('documents.folders.nameLabel')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={renameFolderValue}
                  onChange={e => setRenameFolderValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRenameFolderSubmit(); }}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setRenameFolderTarget(null)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleRenameFolderSubmit} disabled={!renameFolderValue.trim()}>
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirm Modal */}
      {deleteFolderTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('documents.folders.delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteFolderTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p>{t('documents.folders.confirmDelete')}</p>
                <p className="text-muted mb-0">
                  <i className="feather-folder me-2"></i>
                  {deleteFolderTarget.name}
                </p>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setDeleteFolderTarget(null)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="danger" onClick={handleDeleteFolderConfirm}>
                  <i className="feather-trash-2 me-2"></i>
                  {t('documents.folders.delete')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
